import { useCallback, useEffect, useRef, useState } from 'react';
import { formatTaskKey } from '@todo/shared';
import type { TaskSummary } from '@todo/shared';
import { taskApi } from '../api/task.api';
import {
  getReminderDecision,
  rememberDismissed,
  rememberSnoozed,
} from '../lib/reminderSnooze';

/**
 * Reminder pop-ups, bottom-right (Azure/Teams toast position).
 *
 * NO polling: on mount (and whenever the tab regains focus) it fetches the
 * open tasks on your plate that carry a remind_at, and sets a plain
 * setTimeout for each one due in the next 24 hours. At the moment the
 * reminder falls due, the toast slides in - exact to the second, and the API
 * was called exactly once. "Remind in 5 min" is a local snooze (one more
 * timeout); "Dismiss" lets it go - the bell still holds the notification row
 * the server's poller wrote, so nothing is lost.
 */

const SNOOZE_MS = 5 * 60_000;
const HORIZON_MS = 24 * 3_600_000;
/** A reminder that fell due while the app was closed still pops, as long as
 *  it is this fresh - older ones stay in the bell only. */
const CATCH_UP_MS = 30 * 60_000;

interface Toast {
  task: TaskSummary;
  snoozed: boolean;
}

export function ReminderToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  /** Reminders already shown this session - a refetch must not re-arm them. */
  const shown = useRef<Set<string>>(new Set());

  const pop = useCallback((task: TaskSummary, snoozed = false) => {
    timers.current.delete(task.id);
    setToasts((current) =>
      current.some((t) => t.task.id === task.id)
        ? current
        : [...current, { task, snoozed }],
    );
  }, []);

  const arm = useCallback(
    (task: TaskSummary) => {
      if (!task.remindAt || shown.current.has(task.id)) return;
      if (timers.current.has(task.id)) return;

      // A decision from a previous page load (Dismiss / Remind in 5 min)
      // survives reloads - without this, refreshing inside the catch-up
      // window brought the card straight back.
      const decision = getReminderDecision('task', task.id, task.remindAt);
      if (decision === 'dismissed') {
        shown.current.add(task.id);
        return;
      }
      if (typeof decision === 'number') {
        const wait = decision - Date.now();
        if (wait <= 0) {
          if (wait > -CATCH_UP_MS) {
            shown.current.add(task.id);
            pop(task, true);
          }
          return;
        }
        timers.current.set(
          task.id,
          setTimeout(() => {
            shown.current.add(task.id);
            pop(task, true);
          }, wait),
        );
        return;
      }

      const delay = new Date(task.remindAt).getTime() - Date.now();
      if (delay > HORIZON_MS) return;

      // Missed while the tab was closed? Show it NOW rather than never.
      if (delay <= 0) {
        if (delay > -CATCH_UP_MS) {
          shown.current.add(task.id);
          pop(task);
        }
        return;
      }

      timers.current.set(
        task.id,
        setTimeout(() => {
          shown.current.add(task.id);
          pop(task);
        }, delay),
      );
    },
    [pop],
  );

  const load = useCallback(() => {
    taskApi
      .list({ scope: 'assigned-to-me', open: true, pageSize: 100 })
      .then((res) => {
        for (const task of res.data) arm(task);
      })
      .catch(() => undefined);
  }, [arm]);

  useEffect(() => {
    load();
    function onVisible() {
      if (document.visibilityState === 'visible') load();
    }
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', onVisible);
    const pending = timers.current;
    return () => {
      window.removeEventListener('focus', load);
      document.removeEventListener('visibilitychange', onVisible);
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, [load]);

  /** Hide the card (does NOT persist - snooze reuses this). */
  function dismiss(taskId: string) {
    setToasts((current) => current.filter((t) => t.task.id !== taskId));
  }

  /** The Dismiss button: hide AND remember, so a reload stays quiet. */
  function dismissForGood(task: TaskSummary) {
    if (task.remindAt) rememberDismissed('task', task.id, task.remindAt);
    dismiss(task.id);
  }

  function snooze(task: TaskSummary) {
    dismiss(task.id);
    if (task.remindAt) rememberSnoozed('task', task.id, task.remindAt, Date.now() + SNOOZE_MS);
    timers.current.set(
      task.id,
      setTimeout(() => pop(task, true), SNOOZE_MS),
    );
  }

  if (toasts.length === 0) return null;

  return (
    // Positioning lives in AppLayout's shared toast stack - this group only
    // stacks its own cards.
    <div className="pointer-events-none flex flex-col gap-2">
      {toasts.map(({ task, snoozed }) => (
        <div
          key={task.id}
          role="alert"
          className="pointer-events-auto rounded-xl border border-slate-200 bg-white p-4 shadow-xl ring-1 ring-black/5"
        >
          <div className="flex items-start gap-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700">
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4">
                <path d="M8 16a2 2 0 004 0M5 8a5 5 0 0110 0c0 3.5 1.2 4.5 1.2 4.5H3.8S5 11.5 5 8z"
                      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                {snoozed ? 'Reminder · snoozed' : 'Reminder'}
              </p>
              <p className="mt-0.5 truncate text-sm font-medium text-slate-900">
                <span className="font-mono text-xs text-slate-400">{formatTaskKey(task.taskKey)}</span>{' '}
                {task.title}
              </p>
              {task.dueAt && (
                <p className="mt-0.5 text-xs text-slate-500">
                  Due {new Date(task.dueAt).toLocaleString(undefined, {
                    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
                  })}
                </p>
              )}
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => dismissForGood(task)}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
              Dismiss
            </button>
            <button type="button" onClick={() => snooze(task)}
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-amber-600">
              Remind in 5 min
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
